/**
 * Roles a user actually holds, resolved from the roles store (Q-359 A1).
 *
 * `user_metadata.role` is a JWT hint that can be stale or absent; display
 * surfaces (profile page, header menu) must show the role assignments instead.
 * The loose input shape lets the server (`getUnifiedAuth()`: camelCase
 * `platformRoles`) and the client (`UserContext`: snake_case `platform_roles`)
 * pass their lists without an adapter.
 */

export interface HeldRoleLike {
  id?: string;
  slug: string;
  name: string;
  privilege_level?: number | null;
  /** Roles are per tenant: slugs are unique only within one tenant_org_unit. */
  tenant_org_unit_id?: string | null;
}

interface RoleContainerLike {
  roles?: HeldRoleLike[] | null;
}

export interface HeldRolesSource {
  platformRoles?: HeldRoleLike[] | null;
  platform_roles?: HeldRoleLike[] | null;
  agencies?: RoleContainerLike[] | null;
  departments?: RoleContainerLike[] | null;
  /** The flags are the authority for the admin roles (unified.ts derives them from live SYSTEM role rows); stored rows are not. */
  isPlatformAdmin?: boolean | null;
  is_platform_admin?: boolean | null;
  tenantAdminTenantIds?: string[] | null;
  is_tenant_admin?: boolean | null;
}

const ADMIN_PRIVILEGE = 1000; // synthetic admin roles sort above any stored role

/**
 * The platform-admin role has two slugs: 'platform-admin' (DB-seeded) and
 * 'admin-platform' (legacy JWT metadata). They are the same role.
 */
export const PLATFORM_ADMIN_SLUGS: readonly string[] = ['platform-admin', 'admin-platform'];
const PLATFORM_ADMIN_SLUG = 'admin-platform';
const TENANT_ADMIN_SLUG = 'admin-tenant';

export const NO_ROLES_LABEL = 'No roles assigned';
/** Shown while access control is still loading (never "No roles assigned" or "User"). */
export const LOADING_ROLES_LABEL = 'Loading roles';
/** Shown when the access-control context failed to load, so held roles are unknown. */
export const ROLES_UNAVAILABLE_LABEL = 'Roles unavailable';

/**
 * Identity of a held role. Role ids are globally unique, so the id is the key;
 * slugs are unique only per tenant, so slug alone must never merge two roles.
 * A role without an id falls back to tenant + slug + name.
 */
function roleKey(role: HeldRoleLike): string {
  return role.id ? `id:${role.id}` : `${role.tenant_org_unit_id ?? ''}|${role.slug}|${role.name}`;
}

/**
 * Platform + agency + department roles, each distinct role (by id) once,
 * highest privilege first, then by name. Labels are the STORED role names, verbatim.
 * The platform-admin flag is the authority for holding the role; a platform-level
 * stored row (slug platform-admin / admin-platform) is that role, so it is shown
 * under its own name and the flag adds nothing. Only when the flag is set and no
 * such row exists is a synthetic "Platform Admin" added (there is no stored name
 * to show). A tenant custom role using that slug is a different, held role. During masquerade the resolver substitutes the
 * masqueraded roles, so both surfaces show the effective (masqueraded) roles,
 * consistent with the rest of the shell.
 */
export function collectHeldRoles(source: HeldRolesSource | null | undefined): HeldRoleLike[] {
  if (!source) return [];
  const stored = [
    ...(source.platformRoles ?? source.platform_roles ?? []),
    ...(source.agencies ?? []).flatMap((a) => a.roles ?? []),
    ...(source.departments ?? []).flatMap((d) => d.roles ?? []),
  ].filter((r) => !!r?.slug);

  const hasPlatformAdmin = !!(source.isPlatformAdmin || source.is_platform_admin);
  const isPlatformLevelAdminRow = (r: HeldRoleLike) => PLATFORM_ADMIN_SLUGS.includes(r.slug) && !r.tenant_org_unit_id;
  const all = [...stored];
  if (hasPlatformAdmin && !stored.some(isPlatformLevelAdminRow)) {
    all.push({ slug: PLATFORM_ADMIN_SLUG, name: 'Platform Admin', privilege_level: ADMIN_PRIVILEGE });
  }
  const wantsTenantAdmin = (source.tenantAdminTenantIds?.length ?? 0) > 0 || !!source.is_tenant_admin;
  if (wantsTenantAdmin && !all.some((r) => r.slug === TENANT_ADMIN_SLUG)) {
    all.push({ slug: TENANT_ADMIN_SLUG, name: 'Tenant Admin', privilege_level: ADMIN_PRIVILEGE - 1 });
  }

  const byKey = new Map<string, HeldRoleLike>();
  for (const role of all) {
    const key = roleKey(role);
    const seen = byKey.get(key);
    if (!seen || (role.privilege_level ?? 0) > (seen.privilege_level ?? 0)) byKey.set(key, role);
  }
  return [...byKey.values()].sort(
    (a, b) => (b.privilege_level ?? 0) - (a.privilege_level ?? 0) || a.name.localeCompare(b.name),
  );
}

/** Display string: role names joined by ", ", or NO_ROLES_LABEL (never "User"). */
export function formatHeldRoles(roles: HeldRoleLike[]): string {
  return roles.length > 0 ? roles.map((r) => r.name).join(', ') : NO_ROLES_LABEL;
}
